import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useRemoveLineItem } from "@babackapp/return-widget-components/hooks/use-remove-line-item";
import { useReturnableItems } from "@babackapp/return-widget-components/hooks/use-returnable-items";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import {
  hasConflictingResolutions,
  hasIncompleteLineItems,
} from "@babackapp/return-widget-components/lib/draft.lib";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export function useSummary() {
  const { orderId, shopId, shopSlug } = useWidgetParams();
  const navigate = useNavigate();
  const { data: shop } = usePublicShop(shopId);
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const summary = useFinancialSummary(orderId, order ?? null);
  const removeLineItem = useRemoveLineItem(orderId);
  const returnableItems = useReturnableItems(orderId, shopSlug);
  const reasons = useReturnReasons(shopId);

  const oneResolutionPerReturn =
    shop?.preferences?.oneResolutionPerReturn === true;
  const singleArticleOnly = shop?.preferences?.singleArticleReturnOnly === true;
  const hasConflict =
    oneResolutionPerReturn && draft != null && hasConflictingResolutions(draft);
  // BBS-249: a line item can reach the summary with reasonId/quantity set but
  // no resolution picked (e.g. an interrupted resolution step). Never let
  // that item submit silently — see incident on return d0Z6vB10FUxtyEk5J1.
  const isIncomplete = draft != null && hasIncompleteLineItems(draft);

  const draftLineItemIds = new Set(
    draft?.lineItems.map((li) => li.lineItemId) ?? [],
  );
  const remainingReturnableItems = singleArticleOnly
    ? []
    : returnableItems.filter(
        (item) => !draftLineItemIds.has(item.line_item_id),
      );

  const retractationReasonIds = new Set(
    reasons.filter((r) => r.isRetractation).map((r) => r.id),
  );
  const isRetractationItem = (reasonId: string | null) =>
    reasonId !== null && retractationReasonIds.has(reasonId);

  const handleRemove = (lineItemId: string) => {
    const currentCount = draft?.lineItems.length ?? 0;
    removeLineItem(lineItemId);
    if (currentCount <= 1) {
      navigate(`/return-widget/${shopId}/order/${orderId}/line-item`);
    }
  };

  const exchangeItems =
    draft?.lineItems.filter(
      (li) =>
        li.resolution === "exchange" || li.resolution === "exchange-credit",
    ) ?? [];
  const refundItems =
    draft?.lineItems.filter((li) => li.resolution === "refund") ?? [];
  const discountItems =
    draft?.lineItems.filter((li) => li.resolution === "discount") ?? [];
  const orderLineItems = order?.lineItems ?? [];

  return {
    summary,
    shop,
    order,
    hasConflict,
    isIncomplete,
    remainingReturnableItems,
    isRetractationItem,
    handleRemove,
    exchangeItems,
    refundItems,
    discountItems,
    orderLineItems,
  };
}
